<script setup lang="ts">
/**
 * AdminLayout Component
 *
 * Ice Master Club Admin dashboard layout.
 * Dark-themed sidebar + topbar + scrollable content area.
 * Desktop-first, no max-width constraint.
 */

import { ref, computed } from 'vue';
import { RouterView, RouterLink, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const sidebarCollapsed = ref(false);

interface NavItem {
  label: string;
  icon: string;
  to?: string;
  routeName?: string;
  comingSoon?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: 'pi pi-th-large', to: '/admin/dashboard', routeName: 'admin-dashboard' },
  { label: 'Fixtures', icon: 'pi pi-calendar', to: '/admin/fixtures', routeName: 'admin-fixtures' },
  { label: 'Draws', icon: 'pi pi-ticket', to: '/admin/draws', routeName: 'admin-draws' },
  { label: 'Predictions', icon: 'pi pi-chart-bar', comingSoon: true },
  { label: 'Auctions', icon: 'pi pi-megaphone', comingSoon: true },
  { label: 'Fans', icon: 'pi pi-users', to: '/admin/fans', routeName: 'admin-fans' },
  { label: 'Revenue', icon: 'pi pi-pound', to: '/admin/revenue', routeName: 'admin-revenue' },
  { label: 'New Club', icon: 'pi pi-plus-circle', to: '/admin/clubs/new', routeName: 'admin-club-new' },
  { label: 'Settings', icon: 'pi pi-cog', to: '/admin/settings', routeName: 'admin-settings' },
];

function isActive(item: NavItem): boolean {
  if (!item.to) return false;
  return route.path.startsWith(item.to);
}

const breadcrumb = computed(() => {
  const matched = route.matched;
  const parts: string[] = [];
  for (const record of matched) {
    if (record.meta?.title) {
      parts.push(record.meta.title as string);
    }
  }
  return parts.length > 0 ? parts : ['Dashboard'];
});

const adminName = computed(() => authStore.displayName || 'Admin');

function toggleSidebar(): void {
  sidebarCollapsed.value = !sidebarCollapsed.value;
}

async function handleLogout(): Promise<void> {
  await authStore.logout();
  router.push('/login');
}
</script>

<template>
  <div class="flex min-h-screen" style="background-color: #070709;">

    <!-- Sidebar -->
    <aside
      class="fixed top-0 left-0 h-screen flex flex-col z-40 transition-all duration-300 border-r"
      :class="sidebarCollapsed ? 'w-[68px]' : 'w-[240px]'"
      style="background-color: #0F0F14; border-color: #20202C;"
    >
      <!-- Logo + Admin Badge -->
      <div class="flex items-center gap-3 px-4 py-5 border-b" style="border-color: #20202C;">
        <img
          src="/devils-logo.png"
          alt="Cardiff Devils"
          class="flex-shrink-0"
          style="width: 40px; height: 40px; object-fit: contain; filter: drop-shadow(0 0 6px rgba(204,0,0,.4));"
        />
        <div v-if="!sidebarCollapsed" class="flex flex-col">
          <span
            class="text-white text-base leading-tight tracking-wide"
            style="font-family: 'Bebas Neue', sans-serif;"
          >
            Ice Master
          </span>
          <span
            class="text-[12px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded mt-0.5 w-fit"
            style="background-color: #CC0000; color: white; font-family: 'Barlow Condensed', sans-serif;"
          >
            Admin
          </span>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 py-4 overflow-y-auto">
        <ul class="space-y-0.5">
          <li v-for="item in navItems" :key="item.label">
            <!-- Active / normal link -->
            <RouterLink
              v-if="item.to && !item.comingSoon"
              :to="item.to"
              class="flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-all duration-200 group relative"
              :class="isActive(item)
                ? 'text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'"
              :style="isActive(item) ? 'background-color: rgba(204, 0, 0, 0.1);' : ''"
            >
              <!-- Red left accent for active -->
              <div
                v-if="isActive(item)"
                class="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r"
                style="background-color: #CC0000;"
              />
              <i
                :class="item.icon"
                class="text-base w-5 text-center"
                :style="isActive(item) ? 'color: #CC0000;' : ''"
              />
              <span
                v-if="!sidebarCollapsed"
                class="text-sm font-medium"
                :style="{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  color: isActive(item) ? '#CC0000' : undefined,
                }"
              >
                {{ item.label }}
              </span>
            </RouterLink>

            <!-- Coming soon item -->
            <div
              v-else
              class="flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg cursor-not-allowed opacity-40"
            >
              <i :class="item.icon" class="text-base w-5 text-center text-gray-500" />
              <span
                v-if="!sidebarCollapsed"
                class="text-sm font-medium text-gray-500"
                style="font-family: 'Barlow Condensed', sans-serif;"
              >
                {{ item.label }}
              </span>
              <span
                v-if="!sidebarCollapsed"
                class="ml-auto text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style="background-color: #20202C; color: #666; font-family: 'DM Mono', monospace;"
              >
                Soon
              </span>
            </div>
          </li>
        </ul>
      </nav>

      <!-- Collapse toggle -->
      <div class="border-t px-4 py-3" style="border-color: #20202C;">
        <button
          class="flex items-center gap-2 text-gray-500 hover:text-white transition-colors w-full"
          @click="toggleSidebar"
        >
          <i :class="sidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'" class="text-sm" />
          <span
            v-if="!sidebarCollapsed"
            class="text-xs"
            style="font-family: 'DM Sans', sans-serif;"
          >
            Collapse
          </span>
        </button>
      </div>
    </aside>

    <!-- Main area -->
    <div
      class="flex-1 flex flex-col min-h-screen transition-all duration-300"
      :style="{ marginLeft: sidebarCollapsed ? '68px' : '240px' }"
    >
      <!-- Top bar -->
      <header
        class="sticky top-0 z-30 flex items-center justify-between px-6 py-3 border-b"
        style="background-color: #0F0F14; border-color: #20202C;"
      >
        <!-- Breadcrumb -->
        <div class="flex items-center gap-2">
          <span
            v-for="(crumb, i) in breadcrumb"
            :key="i"
            class="text-sm"
            :style="{
              fontFamily: 'Barlow Condensed, sans-serif',
              color: i === breadcrumb.length - 1 ? 'white' : '#666',
            }"
          >
            <span v-if="i > 0" class="mx-1.5" style="color: #333;">/</span>
            {{ crumb }}
          </span>
        </div>

        <!-- Right side: notification bell + user -->
        <div class="flex items-center gap-4">
          <!-- Notification bell -->
          <button
            class="relative p-2 rounded-lg transition-colors hover:bg-white/5"
          >
            <i class="pi pi-bell text-gray-400" />
            <span
              class="absolute top-1 right-1 w-2 h-2 rounded-full"
              style="background-color: #CC0000;"
            />
          </button>

          <!-- User -->
          <div class="flex items-center gap-2.5">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style="background-color: #CC0000; font-family: 'Bebas Neue', sans-serif;"
            >
              {{ adminName.charAt(0).toUpperCase() }}
            </div>
            <span
              class="text-sm text-white hidden lg:block"
              style="font-family: 'DM Sans', sans-serif;"
            >
              {{ adminName }}
            </span>
            <button
              class="text-gray-500 hover:text-white transition-colors ml-1"
              title="Sign out"
              @click="handleLogout"
            >
              <i class="pi pi-sign-out text-sm" />
            </button>
          </div>
        </div>
      </header>

      <!-- Page content -->
      <main class="flex-1 p-6 overflow-y-auto">
        <RouterView />
      </main>
    </div>
  </div>
</template>
